import { DisplayContainer } from '../../../../components/Support/DisplayContainer/DisplayContainer';

import { BottomSheet } from '../../../../components/Toolkit/BottomSheet/BottomSheet';
import { MakeModelProps } from '../../../../components/SearchPage/features/makeModel/MakeModel.typed';
import { useMakeModelContext } from '../../../../components/SearchPage/features/makeModel/MakeModelContext';

import { AddButton } from '../../../../components/SearchPage/features/makeModel/components/AddButton';
import { MakeFilter } from '../../../../components/SearchPage/features/makeModel/components/MakeFilter';
import { InputButton } from '../../../../components/Toolkit/InputButton/InputButton';
import { Text } from '../../../../components/ToolkitV2/Text/Text';
import { Spacer } from '../../../../helpers/genericStyles';
import { useMakeModel } from '../../../../components/SearchPage/features/makeModel/MakeModel.hook';
import { Selected } from '../../../../components/SearchPage/features/makeModel/components/Selected/Selected';
import { ModelFilter } from './components/ModelFilter';
import { TrimFilter } from './components/TrimFilter';
import { Button } from '../../../../components/Toolkit/Button';
import { shouldShowTrims } from './MakeModel.constants';

import * as Styled from './MakeModel.styled';

const MakeModel = (props: MakeModelProps) => {
  // For desktop the dropdowns should not exceed 600px

  const DROPDOWN_MAX_HEIGHT = '600px';
  const {
    mWebConfig,
    webConfig,
    selectMake: onSelectMake,
    selectModel: onSelectModel,
    selectTrim: onSelectTrim,
    maximumAmountOfMakeModelGroupsToBeDisplayed,
    onAddSelection,
    onClearSelection,
    makeOptions,
    heightVariant,
    sectionName,
  } = props;

  const { isMakeModelBottomSheetOpen, makeModels, trimItems } =
    useMakeModelContext();

  const {
    addMake,
    clearMake,
    openBottomSheet,
    closeBottomSheet,
    makeValue,
    selectedModelOptions,
    selectedTrimOptions,
    addModel,
    clearModels,
    addTrim,
    clearTrims,
    modelPlaceholder,
    trimPlaceholder,
    editableMakeModelGroupAvailable,
    isAddAnotherDisabled,
    isModelFilterDisabled,
    isTrimFilterDisabled,
    mappedMakeOptions,
    isModelSheetExpanded,
    setIsModelSheetExpanded,
    isTrimSheetExpanded,
    setIsTrimSheetExpanded,
    allSelectedModels,
  } = useMakeModel({
    onSelectMake,
    onSelectModel,
    onSelectTrim,
    maximumAmountOfMakeModelGroupsToBeDisplayed,
    makeOptions,
    sectionName,
  });

  const showTrims = shouldShowTrims(sectionName);
  const noTrimsAvailable =
    !isTrimFilterDisabled && (!trimItems || trimItems.length === 0);

  return (
    <div data-testid="make-model-dropdowns">
      {makeModels && makeModels.length > 0 && (
        <Selected
          makeModels={makeModels}
          clearSelection={showTrims ? onClearSelection : clearMake}
          heightVariant={heightVariant}
          sectionName={sectionName}
        />
      )}
      {editableMakeModelGroupAvailable && (
        <>
          <DisplayContainer config={mWebConfig} data-testid="mobile-make-model">
            <InputButton
              isActive={isMakeModelBottomSheetOpen}
              value={makeValue}
              onClick={openBottomSheet}
              placeholder="Make"
              heightVariant={heightVariant}
              dataTestId="mobile-make-input-button"
            />
            <BottomSheet
              headerText={
                isMakeModelBottomSheetOpen ? makeValue : modelPlaceholder
              }
              askToClose={() => {
                if (isModelSheetExpanded) {
                  setIsModelSheetExpanded(false);
                } else {
                  closeBottomSheet();
                }
              }}
              isOpen={isMakeModelBottomSheetOpen || isModelSheetExpanded}
              onApply={() => {
                if (isModelSheetExpanded) {
                  setIsModelSheetExpanded(false);
                } else {
                  closeBottomSheet();
                }
              }}
              height="75vh"
              buttonA={
                isModelSheetExpanded ? (
                  <Button ofType="SECONDARY" onClick={clearModels}>
                    Clear
                  </Button>
                ) : undefined
              }
              buttonB={
                isModelSheetExpanded ? (
                  <Button onClick={() => setIsModelSheetExpanded(false)}>
                    Done
                  </Button>
                ) : undefined
              }
            >
              {isMakeModelBottomSheetOpen && (
                <MakeFilter
                  value={makeValue}
                  heightVariant={heightVariant}
                  options={mappedMakeOptions}
                  addMake={(make) => addMake(make, true)}
                />
              )}
              {isModelSheetExpanded && (
                <ModelFilter
                  addModel={addModel}
                  clearModels={clearModels}
                  selected={selectedModelOptions}
                  placeholder={`Search ${makeValue} models`}
                  heightVariant={heightVariant}
                  value={modelPlaceholder}
                  isDisabled={isModelFilterDisabled}
                  disabledModels={allSelectedModels}
                  sectionName={sectionName}
                />
              )}
            </BottomSheet>
            <Spacer marginBottom="M16" />
            <Styled.SInputButton
              placeholder={modelPlaceholder}
              isActive={isModelSheetExpanded}
              value={modelPlaceholder}
              heightVariant={heightVariant}
              isDisabled={isModelFilterDisabled}
              onClick={() => setIsModelSheetExpanded(true)}
              dataTestId="mobile-model-input-button"
            />
            {showTrims && (
              <>
                <Spacer marginBottom="M16" />
                <Styled.SInputButton
                  placeholder={trimPlaceholder}
                  isActive={isTrimSheetExpanded}
                  value={trimPlaceholder}
                  heightVariant={heightVariant}
                  isDisabled={isTrimFilterDisabled}
                  onClick={() => setIsTrimSheetExpanded(true)}
                  dataTestId="mobile-trim-input-button"
                />
                <BottomSheet
                  headerText={trimPlaceholder}
                  askToClose={() => setIsTrimSheetExpanded(false)}
                  isOpen={isTrimSheetExpanded}
                  height="75vh"
                  onApply={() => setIsTrimSheetExpanded(false)}
                  buttonA={
                    <Button ofType="SECONDARY" onClick={clearTrims}>
                      Clear
                    </Button>
                  }
                  buttonB={
                    <Button onClick={() => setIsTrimSheetExpanded(false)}>
                      Done
                    </Button>
                  }
                >
                  {isTrimSheetExpanded && (
                    <TrimFilter
                      addTrim={addTrim}
                      clearTrims={clearTrims}
                      selected={selectedTrimOptions}
                      placeholder={`Search ${makeValue} ${
                        selectedModelOptions[0] || 'model'
                      } trims`}
                      heightVariant={heightVariant}
                      value={trimPlaceholder}
                      isDisabled={isTrimFilterDisabled}
                    />
                  )}
                </BottomSheet>

                {noTrimsAvailable && (
                  <Styled.NoTrimsAvaliable>
                    <Text color="GREY_DARKER" size="L18" weight="bold">
                      No trims available
                    </Text>
                    <Spacer marginBottom="S8" />
                    <Text color="GREY_DARK" size="M16">
                      Please try different make or model
                    </Text>
                  </Styled.NoTrimsAvaliable>
                )}
              </>
            )}
          </DisplayContainer>
          <DisplayContainer config={webConfig} data-testid="desktop-make-model">
            <MakeFilter
              value={makeValue}
              heightVariant={heightVariant}
              options={mappedMakeOptions}
              addMake={addMake}
              maxHeight={DROPDOWN_MAX_HEIGHT}
              showLabel
            />
            <Spacer marginBottom="M16" />
            <ModelFilter
              addModel={addModel}
              clearModels={clearModels}
              selected={selectedModelOptions}
              placeholder={`Search ${makeValue} models`}
              value={modelPlaceholder}
              isDisabled={isModelFilterDisabled}
              heightVariant={heightVariant}
              maxHeight={DROPDOWN_MAX_HEIGHT}
              showLabel
              showFooter
              disabledModels={allSelectedModels}
              sectionName={sectionName}
            />
            {showTrims && (
              <>
                <Spacer marginBottom="M16" />
                <TrimFilter
                  addTrim={addTrim}
                  clearTrims={clearTrims}
                  selected={selectedTrimOptions}
                  placeholder={`Search ${makeValue} ${
                    selectedModelOptions[0] || 'model'
                  } trims`}
                  value={trimPlaceholder}
                  isDisabled={isTrimFilterDisabled}
                  heightVariant={heightVariant}
                  maxHeight={DROPDOWN_MAX_HEIGHT}
                  showLabel
                  showFooter
                />
                {noTrimsAvailable && (
                  <Styled.NoTrimsAvaliable>
                    <Text color="GREY_DARKER" size="L18" weight="bold">
                      No trims available
                    </Text>
                    <Spacer marginBottom="S8" />
                    <Text color="GREY_DARK" size="M16">
                      Please try different make or model
                    </Text>
                  </Styled.NoTrimsAvaliable>
                )}
              </>
            )}
          </DisplayContainer>
          <Spacer marginBottom="M16" />
          <Spacer marginBottom="M16" />
          <AddButton
            disabled={isAddAnotherDisabled}
            onClick={onAddSelection}
            sectionName={sectionName}
            size={heightVariant === 'LARGE' ? 'LARGE' : 'SMALL'}
          />
        </>
      )}
    </div>
  );
};

export { MakeModel };
